import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Check, Crown, Loader2, ArrowLeft, Menu, X, Star, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pricing = () => {
    const { user } = useAuth();
    const { settings, loading: settingsLoading } = useSettings();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const isProUser = user?.subscription_status === 'active';
    const businessName = settings?.business_name || 'BGRemove';
    const logoUrl = settings?.logo_url;

    // Subscription plans (monthly recurring with rollover)
    const subscriptionPlans = [
        { credits: 50, price: 4.99, pricePerCredit: 0.10, popular: false, badge: 'Starter', description: 'Perfect for individuals' },
        { credits: 100, price: 8.99, pricePerCredit: 0.09, popular: true, badge: 'Most Popular', description: 'Best for freelancers' }, // HERO
        { credits: 300, price: 19.99, pricePerCredit: 0.07, popular: false, badge: 'Professional', description: 'For busy creatives' },
        { credits: 500, price: 29.99, pricePerCredit: 0.06, popular: false, badge: 'Business', description: 'For small teams' },
        { credits: 1000, price: 49.99, pricePerCredit: 0.05, popular: false, badge: 'Enterprise', description: 'For large teams' },
    ];

    const handleSubscribe = async (planCredits, planPrice) => {
        if (!user) {
            toast.error('Please login first');
            navigate('/login');
            return;
        }

        // Debug logging
        console.log('User object:', user);
        console.log('isProUser:', isProUser);
        console.log('Current plan credits:', user?.subscription_credits_monthly);
        console.log('Requested plan credits:', planCredits);
        console.log('Subscription status:', user?.subscription_status);

        // Check if user is changing plans
        const currentPlan = user?.subscription_credits_monthly;
        console.log('Current plan value:', currentPlan, 'Type:', typeof currentPlan);

        // User is changing plans if they have an active subscription and a valid current plan
        // Note: currentPlan of 0 means migrated old user, treat as new subscription
        if (isProUser && currentPlan !== undefined && currentPlan !== null && currentPlan > 0) {
            console.log('User has active subscription with plan, allowing plan change');

            if (currentPlan === planCredits) {
                toast.info('You are already on this plan');
                return;
            }

            // User is changing plans - ask for confirmation
            const isUpgrade = planCredits > currentPlan;
            const action = isUpgrade ? 'upgrade' : 'downgrade';
            const confirmMsg = `${action === 'upgrade' ? 'Upgrade' : 'Downgrade'} from ${currentPlan} credits/month to ${planCredits} credits/month?\n\nYou will be ${isUpgrade ? 'charged the prorated difference' : 'credited the prorated difference'} immediately.`;

            console.log('Showing confirmation dialog for plan change');
            if (!window.confirm(confirmMsg)) {
                console.log('User cancelled plan change');
                return;
            }
            console.log('User confirmed plan change, proceeding...');

            // Change plan
            setLoading(true);
            try {
                console.log('Sending plan change request:', { new_credits: planCredits, new_price: planPrice });
                const token = localStorage.getItem('token');
                const response = await axios.post(`${API}/subscription/change-plan`, {
                    new_credits: planCredits,
                    new_price: planPrice
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                console.log('Plan change response:', response.data);
                toast.success(response.data.message || 'Plan changed successfully!');
                // Refresh user data
                window.location.reload();
            } catch (error) {
                console.error('Plan change error:', error);
                console.error('Error response:', error.response);
                const errorMsg = error.response?.data?.detail || error.message || 'Failed to change plan';
                toast.error(`Plan change failed: ${errorMsg}`);
                setLoading(false);
            }
            return;
        }

        // New subscription
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/subscription/create-checkout`, {
                origin_url: window.location.origin,
                plan_type: 'subscription',
                credits: planCredits,
                price: planPrice
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else {
                toast.error('No checkout URL returned');
                setLoading(false);
            }
        } catch (error) {
            console.error('Subscription error:', error);
            console.error('Error response:', error.response?.data);
            const errorMsg = error.response?.data?.detail
                ? (typeof error.response.data.detail === 'string'
                    ? error.response.data.detail
                    : JSON.stringify(error.response.data.detail))
                : 'Failed to create checkout session';
            toast.error(errorMsg);
            setLoading(false);
        }
    };

    if (settingsLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Grain overlay */}
            <div className="grain-overlay" />

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo-link">
                            {logoUrl ? (
                                <img
                                    src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                                    alt={businessName}
                                    className="w-8 h-8 rounded-lg object-contain"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-primary-foreground" />
                                </div>
                            )}
                            <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                        </Link>

                        {/* Desktop nav */}
                        <div className="hidden md:flex items-center gap-6">
                            {user ? (
                                <Button onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="pricing-dashboard-btn">
                                    Dashboard
                                </Button>
                            ) : (
                                <>
                                    <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="pricing-login-link">
                                        Login
                                    </Link>
                                    <Button onClick={() => navigate('/register')} className="rounded-full" data-testid="pricing-get-started-btn">
                                        Get Started
                                    </Button>
                                </>
                            )}
                        </div>

                        {/* Mobile menu button */}
                        <button
                            className="md:hidden p-2"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="pricing-mobile-menu-btn"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-border bg-background p-4 space-y-3">
                        {user ? (
                            <Button onClick={() => navigate('/dashboard')} className="w-full rounded-full" data-testid="pricing-mobile-dashboard-btn">
                                Dashboard
                            </Button>
                        ) : (
                            <>
                                <Link to="/login" className="block text-sm font-medium text-muted-foreground" data-testid="pricing-mobile-login-link">
                                    Login
                                </Link>
                                <Button onClick={() => navigate('/register')} className="w-full rounded-full" data-testid="pricing-mobile-get-started-btn">
                                    Get Started
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </nav>

            {/* Main Content */}
            <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {user && (
                        <Link
                            to="/dashboard"
                            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
                            data-testid="pricing-back-link"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to dashboard
                        </Link>
                    )}

                    <div className="text-center mb-12">
                        <span className="inline-block px-4 py-1.5 rounded-full bg-secondary text-xs font-medium uppercase tracking-widest text-muted-foreground mb-4">
                            Pricing
                        </span>
                        <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">
                            Simple Monthly Pricing
                        </h1>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            Start with 2 free credits. Choose a monthly subscription with rollover credits.
                        </p>
                    </div>

                    {/* Free Plan Highlight */}
                    <div className="max-w-3xl mx-auto mb-12">
                        <div className="glass-card rounded-2xl p-6 sm:p-8 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent shadow-lg hover:shadow-xl transition-all">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <div className="inline-flex items-center gap-2 mb-2">
                                        <h3 className="font-heading font-semibold text-xl sm:text-2xl">Free Plan</h3>
                                        <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-600 text-xs font-bold">$0</span>
                                    </div>
                                    <p className="text-muted-foreground text-sm sm:text-base mb-2">Perfect for trying out the service</p>
                                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-600" /> No credit card required</span>
                                        <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-600" /> Full HD quality</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 sm:gap-6">
                                    <div className="text-center">
                                        <div className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">2</div>
                                        <div className="text-xs text-muted-foreground">Free Credits</div>
                                    </div>
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        onClick={() => navigate(user ? '/dashboard' : '/register')}
                                        className="rounded-full shadow-md hover:shadow-lg transition-all"
                                    >
                                        {user ? 'Go to Dashboard' : 'Start Free'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Subscription Plans */}
                    <div>
                            <div className="text-center mb-8">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
                                    <Star className="w-4 h-4 fill-current" />
                                    <span className="text-sm font-semibold">BEST VALUE - Recommended</span>
                                </div>
                                <div className="space-y-2 text-sm text-muted-foreground">
                                    <p>✓ Unused credits roll over month to month</p>
                                    <p>✓ 14-day money-back guarantee</p>
                                    <p>✓ Cancel, upgrade, or downgrade anytime</p>
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-7xl mx-auto">
                                {subscriptionPlans.map((plan) => (
                                    <div
                                        key={plan.credits}
                                        className={`relative rounded-2xl p-6 border-2 transition-all duration-300 ${
                                            plan.popular
                                                ? 'border-primary bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground shadow-2xl scale-105 hover:scale-110'
                                                : 'border-border bg-card hover:border-primary/50 hover:shadow-xl hover:-translate-y-1'
                                        }`}
                                    >
                                        {plan.popular && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                <span className="px-4 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold uppercase shadow-lg">
                                                    ⭐ {plan.badge}
                                                </span>
                                            </div>
                                        )}

                                        <div className="text-center mb-4">
                                            {!plan.popular && (
                                                <div className="mb-2">
                                                    <span className="text-xs font-semibold text-primary uppercase tracking-wide">{plan.badge}</span>
                                                </div>
                                            )}
                                            <div className={`text-4xl font-bold mb-1 ${plan.popular ? 'text-white' : ''}`}>{plan.credits}</div>
                                            <div className={`text-sm ${plan.popular ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                                                credits/month
                                            </div>
                                            <div className={`text-xs mt-1 ${plan.popular ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                                {plan.description}
                                            </div>
                                        </div>

                                        <div className="text-center mb-6">
                                            <div className={`text-3xl font-bold ${plan.popular ? 'text-white' : ''}`}>${plan.price}</div>
                                            <div className={`text-xs ${plan.popular ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                                USD per month
                                            </div>
                                            <div className={`text-xs mt-2 px-3 py-1 rounded-full inline-block ${
                                                plan.popular
                                                    ? 'bg-white/20 text-white font-semibold'
                                                    : 'bg-primary/10 text-primary font-semibold'
                                            }`}>
                                                ${plan.pricePerCredit.toFixed(2)}/credit
                                            </div>
                                        </div>

                                        <div className="space-y-2 mb-6">
                                            <div className={`flex items-center gap-2 text-xs ${plan.popular ? 'text-primary-foreground/90' : 'text-muted-foreground'}`}>
                                                <Check className={`w-4 h-4 ${plan.popular ? 'text-white' : 'text-green-600'}`} />
                                                <span>Credits rollover monthly</span>
                                            </div>
                                            <div className={`flex items-center gap-2 text-xs ${plan.popular ? 'text-primary-foreground/90' : 'text-muted-foreground'}`}>
                                                <Check className={`w-4 h-4 ${plan.popular ? 'text-white' : 'text-green-600'}`} />
                                                <span>HD quality exports</span>
                                            </div>
                                            <div className={`flex items-center gap-2 text-xs ${plan.popular ? 'text-primary-foreground/90' : 'text-muted-foreground'}`}>
                                                <Check className={`w-4 h-4 ${plan.popular ? 'text-white' : 'text-green-600'}`} />
                                                <span>Cancel anytime</span>
                                            </div>
                                        </div>

                                        <Button
                                            className={`w-full rounded-full transition-all ${
                                                plan.popular
                                                    ? 'bg-white text-primary hover:bg-white/90 shadow-lg hover:shadow-xl'
                                                    : 'shadow-md hover:shadow-lg'
                                            }`}
                                            variant={plan.popular ? 'default' : 'outline'}
                                            onClick={() => handleSubscribe(plan.credits, plan.price)}
                                            disabled={loading || (isProUser && user?.subscription_credits_monthly === plan.credits)}
                                        >
                                            {loading ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : isProUser && user?.subscription_credits_monthly === plan.credits ? (
                                                <>
                                                    <Crown className="w-4 h-4 mr-2" />
                                                    Current Plan
                                                </>
                                            ) : isProUser && user?.subscription_credits_monthly && plan.credits > user.subscription_credits_monthly ? (
                                                '⬆ Upgrade'
                                            ) : isProUser && user?.subscription_credits_monthly && plan.credits < user.subscription_credits_monthly ? (
                                                '⬇ Downgrade'
                                            ) : (
                                                'Subscribe Now'
                                            )}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                    </div>

                    {/* FAQ */}
                    <div className="mt-20 max-w-3xl mx-auto">
                        <h2 className="font-heading font-bold text-2xl text-center mb-8">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-6">
                            <div data-testid="faq-1">
                                <h3 className="font-semibold mb-2">How does the credit system work?</h3>
                                <p className="text-muted-foreground text-sm">
                                    Each background removal uses 1 credit. Subscribe to a monthly plan to get credits that roll over each month as long as you remain subscribed.
                                </p>
                            </div>
                            <div data-testid="faq-2">
                                <h3 className="font-semibold mb-2">Do unused credits roll over?</h3>
                                <p className="text-muted-foreground text-sm">
                                    Yes! All unused credits roll over to the next month as long as you remain subscribed. Your credits never expire while your subscription is active.
                                </p>
                            </div>
                            <div data-testid="faq-3">
                                <h3 className="font-semibold mb-2">Can I cancel my subscription anytime?</h3>
                                <p className="text-muted-foreground text-sm">
                                    Yes! You can cancel, upgrade, or downgrade your subscription at any time. We also offer a 14-day money-back guarantee for new subscriptions.
                                </p>
                            </div>
                            <div data-testid="faq-4">
                                <h3 className="font-semibold mb-2">What payment methods do you accept?</h3>
                                <p className="text-muted-foreground text-sm">
                                    We accept all major credit cards through our secure Stripe payment system.
                                </p>
                            </div>
                            <div data-testid="faq-5">
                                <h3 className="font-semibold mb-2">Is my data secure?</h3>
                                <p className="text-muted-foreground text-sm">
                                    Absolutely. Your images are processed securely and are not stored permanently on our servers.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-border">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        {logoUrl ? (
                            <img
                                src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                                alt={businessName}
                                className="w-6 h-6 rounded object-contain"
                            />
                        ) : (
                            <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
                                <Upload className="w-3 h-3 text-primary-foreground" />
                            </div>
                        )}
                        <span className="font-heading font-semibold">{businessName}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {settings?.footer_text || '© 2024 BGRemove. All rights reserved.'}
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default Pricing;
